import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Users, Lock, Globe, Settings, MoreHorizontal,
  Image as ImageIcon, Video, Send, Heart, MessageCircle,
  UserPlus, UserMinus, Shield, Flag, Trash2, Check, X,
  Loader2, MapPin, Calendar, Bell, Share2, ChevronDown,
  Home, Eye, FileText, AlertTriangle, Clock, Activity,
  BookOpen, MessageSquare, UserCheck, Ban, Edit3, Search,
  Tag, DollarSign, ShoppingBag, Link2, Plus, Download,
  File, FileText as FileDoc, Play, ExternalLink, User,
  Smile, BarChart3, Car, Building, PartyPopper, Sparkles,
  Gift, FileImage, AtSign, Hash, UserCog, ShieldCheck,
  MessageSquareWarning, ClipboardList, AlertOctagon, Timer,
  ScrollText, UserX, Megaphone, Gauge, Info, Star, HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Header from '@/components/Header';
import { ActivityLogViewer, ScheduledPostsManager, ReportedContentViewer } from '@/components/groups';
import api from '@/utils/api';
import { toast } from 'sonner';
import { isHeicFile, convertHeicToJpeg, ACCEPTED_IMAGE_FORMATS } from '@/utils/imageUtils';

const DEFAULT_COVER = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=400&fit=crop";

const GROUP_CATEGORIES = [
  { id: "buy_sell", name: "Buy & Sell", icon: "💰" },
  { id: "collectors", name: "Collectors", icon: "🏆" },
  { id: "local", name: "Local Community", icon: "📍" },
  { id: "fashion", name: "Fashion & Style", icon: "👗" },
  { id: "electronics", name: "Electronics", icon: "📱" },
  { id: "vehicles", name: "Vehicles", icon: "🚗" },
  { id: "home_garden", name: "Home & Garden", icon: "🏠" },
  { id: "sports", name: "Sports & Outdoors", icon: "⚽" },
  { id: "hobbies", name: "Hobbies & Crafts", icon: "🎨" },
  { id: "other", name: "Other", icon: "📦" },
];

// Helper to format relative time
const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
};

// Post Component
const GroupPost = ({ post, groupId, isAdmin, isModerator, onDelete, onLike, user }) => {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const res = await api.get(`/groups/${groupId}/posts/${post.post_id}/comments`);
      setComments(res.data.comments);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleShowComments = () => {
    if (!showComments) {
      fetchComments();
    }
    setShowComments(!showComments);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const res = await api.post(`/groups/${groupId}/posts/${post.post_id}/comments`, {
        text_body: newComment
      });
      setComments([...comments, res.data.comment]);
      setNewComment('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  return (
    <Card className="mb-4 shadow-sm">
      <CardContent className="p-4">
        {/* Post Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={post.user_image} />
              <AvatarFallback className="bg-emerald-100 text-emerald-700">
                {post.user_name?.charAt(0)?.toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-gray-900">{post.user_name}</p>
              <p className="text-xs text-gray-500">{formatRelativeTime(post.created_at)}</p>
            </div>
          </div>
          
          {(isAdmin || isModerator || post.is_owner) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem 
                  className="text-red-600"
                  onClick={() => onDelete(post.post_id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Post
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Post Content - with clickable links */}
        <p className="text-gray-800 mb-3 whitespace-pre-wrap">
          {post.text_body?.split(/(https?:\/\/[^\s]+)/g).map((part, i) => 
            part.match(/^https?:\/\//) ? (
              <a 
                key={i} 
                href={part} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-blue-600 hover:underline break-all"
              >
                {part}
              </a>
            ) : part
          )}
        </p>

        {/* Link Preview */}
        {post.link_preview && (
          <a 
            href={post.link_preview.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block mb-3 border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition bg-white"
          >
            {post.link_preview.image && (
              <div className="h-48 bg-gray-100">
                <img 
                  src={post.link_preview.image} 
                  alt="" 
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
            <div className="p-3">
              <p className="text-xs text-gray-500 uppercase mb-1">{post.link_preview.site_name}</p>
              <p className="font-semibold text-gray-900 line-clamp-2">{post.link_preview.title}</p>
              {post.link_preview.description && (
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{post.link_preview.description}</p>
              )}
            </div>
          </a>
        )}

        {/* Media */}
        {post.media_urls?.length > 0 && (
          <div className={`grid gap-2 mb-3 ${post.media_urls.length === 1 ? '' : 'grid-cols-2'}`}>
            {post.media_urls.map((url, idx) => (
              <img 
                key={idx} 
                src={url} 
                alt="" 
                className="rounded-lg w-full object-cover max-h-96"
              />
            ))}
          </div>
        )}

        {post.video_url && (
          <div className="mb-3">
            <video src={post.video_url} controls className="rounded-lg w-full max-h-96" />
          </div>
        )}

        {/* Attached Listing (For Sale) */}
        {post.attached_listing && (
          <Link 
            to={`/item/${post.attached_listing.listing_id}`}
            className="block mb-3 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg hover:shadow-md transition"
          >
            <div className="flex items-center gap-3">
              {post.attached_listing.images?.[0] && (
                <img 
                  src={post.attached_listing.images[0]} 
                  alt="" 
                  className="w-20 h-20 rounded-lg object-cover"
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-emerald-500 text-white text-xs">
                    <Tag className="h-3 w-3 mr-1" />
                    For Sale
                  </Badge>
                </div>
                <p className="font-semibold text-gray-900 truncate">{post.attached_listing.title}</p>
                <p className="text-lg font-bold text-emerald-600">
                  ${post.attached_listing.price?.toLocaleString()}
                </p>
              </div>
              <ChevronDown className="h-5 w-5 text-gray-400 -rotate-90" />
            </div>
          </Link>
        )}

        {/* Actions */}
        <div className="flex items-center gap-4 pt-3 border-t">
          <button 
            className={`flex items-center gap-1 text-sm ${post.liked_by_me ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`}
            onClick={() => onLike(post.post_id)}
          >
            <Heart className={`h-4 w-4 ${post.liked_by_me ? 'fill-current' : ''}`} />
            <span>{post.like_count || 0}</span>
          </button>
          <button 
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-emerald-600"
            onClick={handleShowComments}
          >
            <MessageCircle className="h-4 w-4" />
            <span>{post.comment_count || 0}</span>
          </button>
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="mt-4 pt-4 border-t">
            {loadingComments ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {comments.map(comment => (
                    <div key={comment.comment_id} className="flex gap-2">
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarImage src={comment.user_image} />
                        <AvatarFallback className="text-xs bg-gray-100">
                          {comment.user_name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="bg-gray-100 rounded-2xl px-3 py-2 flex-1">
                        <p className="text-sm font-semibold">{comment.user_name}</p>
                        <p className="text-sm text-gray-700">{comment.text_body}</p>
                      </div>
                    </div>
                  ))}
                  {comments.length === 0 && (
                    <p className="text-center text-gray-400 text-sm py-4">No comments yet</p>
                  )}
                </div>
                
                <form onSubmit={handleSubmitComment} className="flex gap-2 mt-3">
                  <Avatar className="h-8 w-8 flex-shrink-0">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="text-xs bg-emerald-100 text-emerald-700">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <Input
                    placeholder="Write a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="flex-1 rounded-full bg-gray-100 border-0"
                  />
                  <Button type="submit" size="icon" variant="ghost" disabled={submittingComment || !newComment.trim()}>
                    {submittingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 text-emerald-600" />}
                  </Button>
                </form>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Admin Sidebar Component
const AdminSidebar = ({ group, groupId, joinRequests, isAdmin, isModerator, onNavigate, pendingPosts = [], reportedContent = [] }) => {
  const navigate = useNavigate();
  
  return (
    <div className="w-full lg:w-72 bg-white border-r border-gray-200 min-h-screen">
      {/* Group Mini Info */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-200">
            <img src={group.cover_image || DEFAULT_COVER} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm truncate">{group.name}</h3>
            <p className="text-xs text-gray-500">
              {group.privacy === 'private' ? 'Private' : 'Public'} group · {group.member_count?.toLocaleString()} members
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-2">
        <button
          onClick={() => onNavigate('posts')}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-emerald-600 bg-emerald-50 rounded-lg"
        >
          <Home className="h-5 w-5" />
          Community home
        </button>
        
        <button
          onClick={() => onNavigate('about')}
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg mt-1"
        >
          <Eye className="h-5 w-5" />
          Overview
        </button>
      </nav>

      {/* Admin Tools Section */}
      {(isAdmin || isModerator) && (
        <div className="border-t mt-2">
          <div className="p-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Admin tools</h4>
          </div>
          
          <nav className="px-2 pb-2 space-y-0.5">
            {/* Admin Assist */}
            <button className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
              <Sparkles className="h-5 w-5 text-purple-500" />
              Admin assist
            </button>
            
            {/* Member Requests */}
            <button
              onClick={() => onNavigate('members')}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <span className="flex items-center gap-3">
                <UserCheck className="h-5 w-5 text-gray-500" />
                Member requests
              </span>
              {joinRequests.length > 0 && (
                <Badge className="bg-red-500 text-white h-5 min-w-[20px] flex items-center justify-center text-xs">
                  {joinRequests.length}
                </Badge>
              )}
            </button>

            {/* Badge Requests */}
            <button className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
              <Star className="h-5 w-5 text-gray-500" />
              Badge requests
            </button>

            {/* Membership Questions */}
            <button className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
              <HelpCircle className="h-5 w-5 text-gray-500" />
              Membership questions
            </button>

            {/* Pending Posts */}
            <button 
              onClick={() => onNavigate('pending-posts')}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <span className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-gray-500" />
                Pending posts
              </span>
              {pendingPosts.length > 0 && (
                <Badge className="bg-amber-500 text-white h-5 min-w-[20px] flex items-center justify-center text-xs">
                  {pendingPosts.length}
                </Badge>
              )}
            </button>

            {/* Potential Spam */}
            <button 
              onClick={() => onNavigate('spam')}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Potential spam
            </button>

            {/* Scheduled Posts */}
            <button 
              onClick={() => onNavigate('scheduled-posts')}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Timer className="h-5 w-5 text-gray-500" />
              Scheduled posts
            </button>

            {/* Activity Log */}
            <button 
              onClick={() => onNavigate('activity-log')}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Activity className="h-5 w-5 text-gray-500" />
              Activity log
            </button>

            {/* Group Rules */}
            <button 
              onClick={() => navigate(`/community/groups/${groupId}/settings`)}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <ScrollText className="h-5 w-5 text-gray-500" />
              Group rules
            </button>

            {/* Member-reported Content */}
            <button 
              onClick={() => onNavigate('reported-content')}
              className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <span className="flex items-center gap-3">
                <Flag className="h-5 w-5 text-red-500" />
                Member-reported content
              </span>
              {reportedContent.length > 0 && (
                <Badge className="bg-red-500 text-white h-5 min-w-[20px] flex items-center justify-center text-xs">
                  {reportedContent.length}
                </Badge>
              )}
            </button>

            {/* Moderation Alerts */}
            <button 
              onClick={() => onNavigate('moderation-alerts')}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <AlertOctagon className="h-5 w-5 text-orange-500" />
              Moderation alerts
            </button>

            {/* Group Status */}
            <button 
              onClick={() => onNavigate('group-status')}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <Gauge className="h-5 w-5 text-blue-500" />
              Group status
            </button>

            {isAdmin && (
              <button
                onClick={() => navigate(`/community/groups/${groupId}/settings`)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg mt-2 border-t pt-4"
              >
                <Settings className="h-5 w-5 text-gray-500" />
                Group settings
              </button>
            )}
          </nav>
        </div>
      )}
    </div>
  );
};

// Main Group Detail Page
const GroupDetailPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMember, setIsMember] = useState(false);
  const [myRole, setMyRole] = useState(null);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [canViewContent, setCanViewContent] = useState(false);
  
  // Posts state
  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [newPost, setNewPost] = useState('');
  const [postMediaUrls, setPostMediaUrls] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [submittingPost, setSubmittingPost] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [userListings, setUserListings] = useState([]);
  const [showListingPicker, setShowListingPicker] = useState(false);
  const [linkPreview, setLinkPreview] = useState(null);
  const [fetchingLinkPreview, setFetchingLinkPreview] = useState(false);
  
  // Members state
  const [members, setMembers] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  
  // Events state
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '', description: '', start_date: '', end_date: '',
    location: '', is_online: false, online_link: '', max_attendees: ''
  });
  const [submittingEvent, setSubmittingEvent] = useState(false);
  
  // Media state
  const [media, setMedia] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [selectedMediaItem, setSelectedMediaItem] = useState(null);
  
  // Files state
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [fileForm, setFileForm] = useState({ name: '', url: '', file_type: 'document' });
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Activity notifications state
  const [activityNotifications, setActivityNotifications] = useState([]);
  
  // Invite dialog
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteUserIds, setInviteUserIds] = useState('');
  
  // Search state
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // More options menu
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  
  // Active content section
  const [activeSection, setActiveSection] = useState('posts');

  // Enhanced Post Composer Dialog
  const [showPostComposer, setShowPostComposer] = useState(false);
  const [postType, setPostType] = useState('discussion'); // discussion, sale, poll
  const [showAddToPostMenu, setShowAddToPostMenu] = useState(false);
  const [feelingActivity, setFeelingActivity] = useState(null);
  const [checkInLocation, setCheckInLocation] = useState('');
  const [pollOptions, setPollOptions] = useState(['', '']);
  const [taggedPeople, setTaggedPeople] = useState([]);
  const [taggedEvent, setTaggedEvent] = useState(null);
  
  // Create Listing Dialog
  const [showCreateListingDialog, setShowCreateListingDialog] = useState(false);
  const [listingType, setListingType] = useState(null); // item, vehicle, home, sale_event
  const [newListingData, setNewListingData] = useState({
    title: '',
    description: '',
    price: '',
    category: '',
    condition: 'new',
    images: [],
    location: ''
  });
  const [creatingListing, setCreatingListing] = useState(false);
  
  // Buy and Sell tab
  const [buyAndSellPosts, setBuyAndSellPosts] = useState([]);
  const [loadingBuyAndSell, setLoadingBuyAndSell] = useState(false);
  
  // Featured posts
  const [featuredPosts, setFeaturedPosts] = useState([]);
  
  // Admin sidebar state
  const [showAdminSidebar, setShowAdminSidebar] = useState(true);
  const [memberRequests, setMemberRequests] = useState([]);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [reportedContent, setReportedContent] = useState([]);

  // WebSocket ref for group notifications
  const wsRef = useRef(null);
  const linkPreviewDebounceRef = useRef(null);

  const isAdmin = myRole === 'admin';
  const isModerator = myRole === 'moderator';

  // WebSocket connection for real-time group notifications
  useEffect(() => {
    if (!user || !groupId) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // Construct WebSocket URL
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsHost = apiUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    const wsUrl = `${wsProtocol}//${wsHost}/api/groups/ws/${user.user_id}?token=${token}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Group notifications WebSocket connected');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Only show notification if it's for the current group
          if (data.group_id === groupId) {
            // Show toast notification based on type
            if (data.type === 'new_post') {
              toast.success(`${data.user_name} posted in ${data.group_name}`, {
                description: data.preview,
                action: {
                  label: 'View',
                  onClick: () => fetchPosts()
                }
              });
              // Refresh posts in background
              fetchPosts();
            } else if (data.type === 'new_member') {
              toast.info(`${data.user_name} joined the group`);
              // Refresh member count
              fetchGroup();
            } else if (data.type === 'new_event') {
              toast.info(`New event: ${data.event_title}`, {
                description: `Created by ${data.user_name}`
              });
              if (activeSection === 'events') fetchEvents();
            }
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onclose = () => {
        console.log('Group notifications WebSocket disconnected');
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      // Keep-alive ping every 30 seconds
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);

      return () => {
        clearInterval(pingInterval);
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      };
    } catch (err) {
      console.error('Failed to establish WebSocket connection:', err);
    }
  }, [user, groupId, activeSection]);

  // Debounced link preview fetching
  useEffect(() => {
    // Clear any existing timeout
    if (linkPreviewDebounceRef.current) {
      clearTimeout(linkPreviewDebounceRef.current);
    }

    // Reset preview if post is empty
    if (!newPost.trim()) {
      setLinkPreview(null);
      setFetchingLinkPreview(false);
      return;
    }

    // Extract URL from text
    const urlPattern = /https?:\/\/[^\s<>"{}|\\^`\[\]]+/g;
    const urls = newPost.match(urlPattern);
    
    if (!urls || urls.length === 0) {
      setLinkPreview(null);
      return;
    }

    const firstUrl = urls[0];

    // If we already have a preview for this URL, don't fetch again
    if (linkPreview && linkPreview.url === firstUrl) {
      return;
    }

    // Debounce the fetch by 500ms
    linkPreviewDebounceRef.current = setTimeout(async () => {
      setFetchingLinkPreview(true);
      try {
        const res = await api.get('/link-preview', { params: { url: firstUrl } });
        if (res.data.success && res.data.preview) {
          setLinkPreview(res.data.preview);
        } else {
          setLinkPreview(null);
        }
      } catch (error) {
        console.log('Failed to fetch link preview');
        setLinkPreview(null);
      } finally {
        setFetchingLinkPreview(false);
      }
    }, 500);

    return () => {
      if (linkPreviewDebounceRef.current) {
        clearTimeout(linkPreviewDebounceRef.current);
      }
    };
  }, [newPost]);

  // Search posts in group
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      // Filter posts locally based on search query
      const filtered = posts.filter(post => 
        post.text_body?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.user_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  // Fetch user's listings for attaching to posts
  useEffect(() => {
    const fetchUserListings = async () => {
      if (!user) return;
      try {
        const res = await api.get(`/listings?seller_id=${user.user_id}&status=active&limit=50`);
        setUserListings(res.data.listings || []);
      } catch (error) {
        console.log('Failed to fetch user listings');
      }
    };
    fetchUserListings();
  }, [user]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get('/auth/me');
        setUser(res.data);
      } catch {
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  const fetchGroup = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/groups/${groupId}`);
      setGroup(res.data.group);
      setIsMember(res.data.is_member);
      setMyRole(res.data.my_role);
      setMembershipStatus(res.data.membership_status);
      setCanViewContent(res.data.can_view_content);
    } catch (error) {
      toast.error('Group not found');
      navigate('/community/groups');
    } finally {
      setLoading(false);
    }
  }, [groupId, navigate]);

  const fetchPosts = useCallback(async () => {
    if (!canViewContent) return;
    setLoadingPosts(true);
    try {
      const res = await api.get(`/groups/${groupId}/posts`);
      setPosts(res.data.posts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoadingPosts(false);
    }
  }, [groupId, canViewContent]);

  const fetchMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const res = await api.get(`/groups/${groupId}/members`);
      setMembers(res.data.members);
      
      if (isAdmin || isModerator) {
        const requestsRes = await api.get(`/groups/${groupId}/join-requests`);
        setJoinRequests(requestsRes.data.requests);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoadingMembers(false);
    }
  }, [groupId, isAdmin, isModerator]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  useEffect(() => {
    if (canViewContent) {
      fetchPosts();
    }
  }, [fetchPosts, canViewContent]);

  useEffect(() => {
    if (isMember) {
      fetchMembers();
    }
  }, [isMember, fetchMembers]);

  // Fetch events when tab is active
  const fetchEvents = useCallback(async () => {
    if (!canViewContent) return;
    setLoadingEvents(true);
    try {
      const res = await api.get(`/groups/${groupId}/events`);
      setEvents(res.data.events || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoadingEvents(false);
    }
  }, [groupId, canViewContent]);

  // Fetch media when tab is active
  const fetchMedia = useCallback(async () => {
    if (!canViewContent) return;
    setLoadingMedia(true);
    try {
      const res = await api.get(`/groups/${groupId}/media`);
      setMedia(res.data.media || []);
    } catch (error) {
      console.error('Error fetching media:', error);
    } finally {
      setLoadingMedia(false);
    }
  }, [groupId, canViewContent]);

  // Fetch files when tab is active
  const fetchFiles = useCallback(async () => {
    if (!canViewContent) return;
    setLoadingFiles(true);
    try {
      const res = await api.get(`/groups/${groupId}/files`);
      setFiles(res.data.files || []);
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoadingFiles(false);
    }
  }, [groupId, canViewContent]);

  // Fetch buy and sell posts
  const fetchBuyAndSellPosts = useCallback(async () => {
    if (!canViewContent) return;
    setLoadingBuyAndSell(true);
    try {
      const res = await api.get(`/groups/${groupId}/posts`, {
        params: { type: 'sale' }
      });
      // Filter posts with attached listings
      const salePosts = (res.data.posts || []).filter(p => p.attached_listing || p.is_sale_post);
      setBuyAndSellPosts(salePosts);
    } catch (error) {
      console.error('Error fetching buy and sell posts:', error);
    } finally {
      setLoadingBuyAndSell(false);
    }
  }, [groupId, canViewContent]);

  // Fetch featured posts
  const fetchFeaturedPosts = useCallback(async () => {
    if (!canViewContent) return;
    try {
      const res = await api.get(`/groups/${groupId}/posts`, {
        params: { featured: true }
      });
      setFeaturedPosts(res.data.posts || []);
    } catch (error) {
      console.error('Error fetching featured posts:', error);
    }
  }, [groupId, canViewContent]);

  // Create listing from group
  const handleCreateListing = async () => {
    if (!newListingData.title || !newListingData.price) {
      toast.error('Please fill in title and price');
      return;
    }
    
    setCreatingListing(true);
    try {
      // Create the listing on BidZuu main site
      const listingPayload = {
        title: newListingData.title,
        description: newListingData.description,
        price: parseFloat(newListingData.price),
        category: newListingData.category || 'other',
        condition: newListingData.condition,
        images: newListingData.images,
        location: newListingData.location || group?.location || '',
        listing_type: listingType,
        from_group: groupId,
        group_name: group?.name
      };
      
      const listingRes = await api.post('/listings', listingPayload);
      const newListing = listingRes.data.listing;
      
      // Also create a post in the group with this listing attached
      const postPayload = {
        text_body: `🛒 ${listingType === 'vehicle' ? '🚗' : listingType === 'home' ? '🏠' : listingType === 'sale_event' ? '🎉' : '📦'} For Sale: ${newListingData.title}\n\n${newListingData.description}`,
        media_urls: newListingData.images,
        listing_id: newListing.listing_id,
        is_sale_post: true
      };
      
      const postRes = await api.post(`/groups/${groupId}/posts`, postPayload);
      setPosts([postRes.data.post, ...posts]);
      
      toast.success('Listing created and posted to group!');
      setShowCreateListingDialog(false);
      setListingType(null);
      setNewListingData({
        title: '',
        description: '',
        price: '',
        category: '',
        condition: 'new',
        images: [],
        location: ''
      });
      
      // Refresh buy and sell tab
      if (activeSection === 'buyandsell') {
        fetchBuyAndSellPosts();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    } finally {
      setCreatingListing(false);
    }
  };

  // Handle listing image upload
  const handleListingImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (newListingData.images.length + files.length > 8) {
      toast.error('Maximum 8 images allowed');
      return;
    }

    for (const file of files) {
      try {
        let processedFile = file;
        if (isHeicFile(file)) {
          processedFile = await convertHeicToJpeg(file);
        }
        const formData = new FormData();
        formData.append('file', processedFile);
        const res = await api.post('/upload', formData);
        setNewListingData(prev => ({
          ...prev,
          images: [...prev.images, res.data.url]
        }));
      } catch (error) {
        toast.error('Failed to upload image');
      }
    }
  };

  // Load content based on active section
  useEffect(() => {
    if (activeSection === 'events' && events.length === 0) fetchEvents();
    if (activeSection === 'media' && media.length === 0) fetchMedia();
    if (activeSection === 'files' && files.length === 0) fetchFiles();
    if (activeSection === 'buyandsell' && buyAndSellPosts.length === 0) fetchBuyAndSellPosts();
    if (activeSection === 'featured' && featuredPosts.length === 0) fetchFeaturedPosts();
  }, [activeSection, fetchEvents, fetchMedia, fetchFiles, fetchBuyAndSellPosts, fetchFeaturedPosts, events.length, media.length, files.length, buyAndSellPosts.length, featuredPosts.length]);

  const handleJoin = async () => {
    try {
      const res = await api.post(`/groups/${groupId}/join`);
      toast.success(res.data.message);
      setMembershipStatus(res.data.status);
      if (res.data.status === 'active') {
        setIsMember(true);
        setMyRole('member');
        setCanViewContent(true);
        fetchGroup();
        fetchPosts();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const handleLeave = async () => {
    if (!confirm('Are you sure you want to leave this group?')) return;
    try {
      await api.post(`/groups/${groupId}/leave`);
      toast.success('Left the group');
      setIsMember(false);
      setMyRole(null);
      fetchGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave');
    }
  };

  const handleShareGroup = async () => {
    const shareUrl = window.location.href;
    const shareTitle = group?.name || 'Check out this group on BidZuu';
    const shareText = group?.description || `Join ${group?.name} on BidZuu!`;
    
    // Try Web Share API first (mobile-friendly)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        });
        return;
      } catch (error) {
        // User cancelled or share failed, continue to clipboard fallback
        if (error.name === 'AbortError') return;
      }
    }
    
    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      // Final fallback using execCommand for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Link copied to clipboard!');
      } catch (e) {
        toast.error('Unable to copy link. Please copy the URL manually.');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (postMediaUrls.length + files.length > 4) {
      toast.error('Maximum 4 images allowed');
      return;
    }

    setUploadingMedia(true);
    for (const file of files) {
      try {
        let processedFile = file;
        if (isHeicFile(file)) {
          processedFile = await convertHeicToJpeg(file);
        }
        const formData = new FormData();
        formData.append('file', processedFile);
        const res = await api.post('/upload', formData);
        setPostMediaUrls(prev => [...prev, res.data.url]);
      } catch (error) {
        toast.error('Failed to upload image');
      }
    }
    setUploadingMedia(false);
  };

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    if (!newPost.trim() && postMediaUrls.length === 0 && !selectedListing) return;

    setSubmittingPost(true);
    try {
      const res = await api.post(`/groups/${groupId}/posts`, {
        text_body: newPost,
        media_urls: postMediaUrls,
        listing_id: selectedListing?.listing_id || null,
        link_preview: linkPreview
      });
      setPosts([res.data.post, ...posts]);
      setNewPost('');
      setPostMediaUrls([]);
      setSelectedListing(null);
      setLinkPreview(null);
      toast.success('Post created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setSubmittingPost(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm('Delete this post?')) return;
    try {
      await api.delete(`/groups/${groupId}/posts/${postId}`);
      setPosts(posts.filter(p => p.post_id !== postId));
      setPendingPosts(pendingPosts.filter(p => p.post_id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleApprovePost = async (postId) => {
    try {
      await api.post(`/groups/${groupId}/posts/${postId}/approve`);
      setPendingPosts(pendingPosts.filter(p => p.post_id !== postId));
      fetchPosts();
      toast.success('Post approved');
    } catch (error) {
      toast.error('Failed to approve post');
    }
  };

  const handleMarkNotSpam = async (postId) => {
    try {
      await api.post(`/groups/${groupId}/posts/${postId}/mark-not-spam`);
      setPosts(posts.map(p => 
        p.post_id === postId ? { ...p, is_spam: false, spam_reason: null } : p
      ));
      toast.success('Post marked as not spam');
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const handleLikePost = async (postId) => {
    try {
      const res = await api.post(`/groups/${groupId}/posts/${postId}/like`);
      setPosts(posts.map(p => 
        p.post_id === postId 
          ? { ...p, like_count: res.data.like_count, liked_by_me: res.data.action === 'liked' }
          : p
      ));
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleApproveRequest = async (userId) => {
    try {
      await api.post(`/groups/${groupId}/members/${userId}/approve`);
      setJoinRequests(joinRequests.filter(r => r.user_id !== userId));
      fetchMembers();
      fetchGroup();
      toast.success('Request approved');
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const handleRejectRequest = async (userId) => {
    try {
      await api.post(`/groups/${groupId}/members/${userId}/reject`);
      setJoinRequests(joinRequests.filter(r => r.user_id !== userId));
      toast.success('Request rejected');
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!confirm('Remove this member from the group?')) return;
    try {
      await api.post(`/groups/${groupId}/members/${userId}/remove`);
      fetchMembers();
      fetchGroup();
      toast.success('Member removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleUpdateRole = async (userId, role) => {
    try {
      await api.post(`/groups/${groupId}/members/${userId}/role?role=${role}`);
      fetchMembers();
      toast.success(`Role updated to ${role}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  // Event handlers
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!eventForm.title.trim() || !eventForm.start_date) {
      toast.error('Event title and start date are required');
      return;
    }
    
    setSubmittingEvent(true);
    try {
      const res = await api.post(`/groups/${groupId}/events`, {
        ...eventForm,
        max_attendees: eventForm.max_attendees ? parseInt(eventForm.max_attendees) : null
      });
      setEvents([res.data.event, ...events]);
      setShowEventDialog(false);
      setEventForm({ title: '', description: '', start_date: '', end_date: '', location: '', is_online: false, online_link: '', max_attendees: '' });
      toast.success('Event created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setSubmittingEvent(false);
    }
  };

  const handleRsvpEvent = async (eventId, status) => {
    try {
      const res = await api.post(`/groups/${groupId}/events/${eventId}/rsvp?status=${status}`);
      setEvents(events.map(e => 
        e.event_id === eventId 
          ? { ...e, my_rsvp: status, attendee_count: res.data.attendee_count }
          : e
      ));
      toast.success(`RSVP updated: ${status}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to RSVP');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Delete this event?')) return;
    try {
      await api.delete(`/groups/${groupId}/events/${eventId}`);
      setEvents(events.filter(e => e.event_id !== eventId));
      toast.success('Event deleted');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // File handlers
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await api.post('/upload', formData);
      
      const fileType = file.name.endsWith('.pdf') ? 'pdf' 
        : file.name.match(/\.(doc|docx)$/) ? 'document'
        : file.name.match(/\.(xls|xlsx)$/) ? 'spreadsheet'
        : file.name.match(/\.(ppt|pptx)$/) ? 'presentation'
        : 'other';
      
      const res = await api.post(`/groups/${groupId}/files`, {
        name: file.name,
        url: uploadRes.data.url,
        file_type: fileType,
        size: file.size
      });
      
      setFiles([res.data.file, ...files]);
      toast.success('File uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!confirm('Delete this file?')) return;
    try {
      await api.delete(`/groups/${groupId}/files/${fileId}`);
      setFiles(files.filter(f => f.file_id !== fileId));
      toast.success('File deleted');
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Format event date
  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header user={user} setUser={setUser} />
        <div className="h-[184px]"></div>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  if (!group) return null;

  const category = GROUP_CATEGORIES.find(c => c.id === group.category) || GROUP_CATEGORIES[GROUP_CATEGORIES.length - 1];

  // Render content based on active section
  const renderContent = () => {
    // About Tab
    if (activeSection === 'about') {
      return (
        <div className="space-y-6">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <Info className="h-5 w-5 text-blue-600" />
                About this group
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">Description</h3>
                <p className="text-gray-600">{group.description || 'No description available.'}</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  {group.privacy === 'private' ? (
                    <Lock className="h-5 w-5 text-gray-500 mt-0.5" />
                  ) : (
                    <Globe className="h-5 w-5 text-gray-500 mt-0.5" />
                  )}
                  <div>
                    <p className="font-medium">{group.privacy === 'private' ? 'Private' : 'Public'}</p>
                    <p className="text-sm text-gray-500">
                      {group.privacy === 'private' 
                        ? 'Only members can see who\'s in the group and what they post.'
                        : 'Anyone can see who\'s in the group and what they post.'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Eye className="h-5 w-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Visible</p>
                    <p className="text-sm text-gray-500">Anyone can find this group.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="font-medium">{group.member_count?.toLocaleString()} members</p>
                    <p className="text-sm text-gray-500">Growing community</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Created {formatRelativeTime(group.created_at)}</p>
                    <p className="text-sm text-gray-500">Group history</p>
                  </div>
                </div>
              </div>
              
              {group.rules && group.rules.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <ScrollText className="h-4 w-4" />
                    Group Rules
                  </h3>
                  <div className="space-y-2">
                    {group.rules.map((rule, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <p className="font-medium">{idx + 1}. {rule.title}</p>
                        <p className="text-sm text-gray-600">{rule.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    // Buy and Sell Tab
    if (activeSection === 'buyandsell') {
      return (
        <div className="space-y-4">
          {/* Create Listing Box */}
          {isMember && (
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <div className="flex gap-3 mb-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-emerald-100 text-emerald-700">
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => setShowCreateListingDialog(true)}
                    className="flex-1 text-left px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-500 transition-colors"
                  >
                    What are you selling?
                  </button>
                </div>
                
                <div className="flex items-center justify-around pt-2 border-t">
                  <Button 
                    variant="ghost" 
                    className="flex-1 text-gray-600 hover:bg-gray-100"
                    onClick={() => {
                      setListingType('item');
                      setShowCreateListingDialog(true);
                    }}
                  >
                    <Tag className="h-5 w-5 mr-2 text-teal-500" />
                    Item for sale
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="flex-1 text-gray-600 hover:bg-gray-100"
                    onClick={() => {
                      setListingType('vehicle');
                      setShowCreateListingDialog(true);
                    }}
                  >
                    <Car className="h-5 w-5 mr-2 text-blue-500" />
                    Vehicle
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="flex-1 text-gray-600 hover:bg-gray-100 hidden sm:flex"
                    onClick={() => {
                      setListingType('home');
                      setShowCreateListingDialog(true);
                    }}
                  >
                    <Building className="h-5 w-5 mr-2 text-orange-500" />
                    Home
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Sale Listings */}
          {loadingBuyAndSell ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : buyAndSellPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {buyAndSellPosts.map(post => (
                <Card key={post.post_id} className="overflow-hidden hover:shadow-md transition-shadow">
                  {(post.attached_listing?.images?.[0] || post.media_urls?.[0]) && (
                    <div className="aspect-square bg-gray-100">
                      <img 
                        src={post.attached_listing?.images?.[0] || post.media_urls?.[0]} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="p-4">
                    <p className="font-bold text-lg text-emerald-600">
                      ${(post.attached_listing?.price || 0).toLocaleString()}
                    </p>
                    <p className="font-medium text-gray-900 line-clamp-2">
                      {post.attached_listing?.title || post.text_body}
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                      <Avatar className="h-6 w-6">
                        <AvatarImage src={post.user_image} />
                        <AvatarFallback className="text-xs">{post.user_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <span>{post.user_name}</span>
                      <span>·</span>
                      <span>{formatRelativeTime(post.created_at)}</span>
                    </div>
                    {post.attached_listing && (
                      <Link to={`/item/${post.attached_listing.listing_id}`}>
                        <Button className="w-full mt-3 bg-blue-600 hover:bg-blue-700" size="sm">
                          View Listing
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <ShoppingBag className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="font-semibold mb-2">No items for sale yet</h3>
              <p className="text-gray-500 mb-4">Be the first to list something for sale in this group.</p>
              {isMember && (
                <Button onClick={() => setShowCreateListingDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Listing
                </Button>
              )}
            </Card>
          )}
        </div>
      );
    }

    // Featured Tab
    if (activeSection === 'featured') {
      return (
        <div className="space-y-4">
          <Card className="p-6 bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Star className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold">Featured</h3>
                <p className="text-sm text-gray-600">Highlights and pinned content from this group</p>
              </div>
            </div>
            {isAdmin && (
              <Button variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Add to Featured
              </Button>
            )}
          </Card>
          
          {featuredPosts.length > 0 ? (
            <div className="space-y-4">
              {featuredPosts.map(post => (
                <GroupPost 
                  key={post.post_id} 
                  post={post} 
                  groupId={groupId}
                  isAdmin={isAdmin}
                  isModerator={isModerator}
                  onDelete={handleDeletePost}
                  onLike={handleLikePost}
                  user={user}
                />
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <Star className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="font-semibold mb-2">No featured content yet</h3>
              <p className="text-gray-500">Admins can highlight the best posts, rules, and announcements here.</p>
            </Card>
          )}
        </div>
      );
    }

    if (activeSection === 'members') {
      return (
        <div className="space-y-4">
          {/* Join Requests (Admin/Moderator only) */}
          {(isAdmin || isModerator) && joinRequests.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-emerald-600" />
                  Join Requests ({joinRequests.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {joinRequests.map(request => (
                    <div key={request.user_id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={request.user_image} />
                          <AvatarFallback>{request.user_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{request.user_name}</p>
                          <p className="text-xs text-gray-500">
                            Requested {formatRelativeTime(request.requested_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleApproveRequest(request.user_id)}
                          className="bg-emerald-600"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleRejectRequest(request.user_id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Members List */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">All Members ({group.member_count})</CardTitle>
            </CardHeader>
            <CardContent>
              {loadingMembers ? (
                <div className="flex justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <div className="space-y-2">
                  {members.map(member => (
                    <div key={member.user_id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarImage src={member.user_image} />
                          <AvatarFallback>{member.user_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium flex items-center gap-2">
                            {member.user_name}
                            {member.role === 'admin' && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs">Admin</Badge>
                            )}
                            {member.role === 'moderator' && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">Moderator</Badge>
                            )}
                          </p>
                          <p className="text-xs text-gray-500">
                            Joined {formatRelativeTime(member.joined_at)}
                          </p>
                        </div>
                      </div>
                      
                      {isAdmin && member.role !== 'admin' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {member.role === 'member' ? (
                              <DropdownMenuItem onClick={() => handleUpdateRole(member.user_id, 'moderator')}>
                                <Shield className="h-4 w-4 mr-2" />
                                Make Moderator
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleUpdateRole(member.user_id, 'member')}>
                                <UserMinus className="h-4 w-4 mr-2" />
                                Remove Moderator
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleRemoveMember(member.user_id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove from Group
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    // Events view
    if (activeSection === 'events') {
      return (
        <div className="space-y-4">
          {/* Create Event Button - For logged-in users who can view content */}
          {user && canViewContent && (
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <Button 
                  onClick={() => setShowEventDialog(true)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Event
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Sign in prompt for non-logged-in users */}
          {!user && canViewContent && (
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => navigate('/auth')}
                >
                  Sign in to Create Events
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Events List */}
          {loadingEvents ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : events.length > 0 ? (
            events.map(event => (
              <Card key={event.event_id} className="shadow-sm overflow-hidden">
                {event.cover_image && (
                  <div className="h-32 bg-gray-200">
                    <img src={event.cover_image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-emerald-600 font-medium mb-1">
                        {formatEventDate(event.start_date)}
                      </p>
                      <h3 className="text-lg font-semibold text-gray-900">{event.title}</h3>
                      {event.description && (
                        <p className="text-gray-600 text-sm mt-1 line-clamp-2">{event.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                        {event.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {event.location}
                          </span>
                        )}
                        {event.is_online && event.online_link && (
                          <a href={event.online_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-600 hover:underline">
                            <ExternalLink className="h-4 w-4" />
                            Online Event
                          </a>
                        )}
                        <span className="flex items-center gap-1">
                          <Users className="h-4 w-4" />
                          {event.attendee_count} attending
                        </span>
                      </div>
                    </div>
                    {/* Delete button - Admin only */}
                    {isAdmin && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDeleteEvent(event.event_id)}
                        title="Delete event (Admin only)"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button 
                      size="sm" 
                      variant={event.my_rsvp === 'going' ? 'default' : 'outline'}
                      onClick={() => handleRsvpEvent(event.event_id, 'going')}
                      className={event.my_rsvp === 'going' ? 'bg-emerald-600' : ''}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      Going
                    </Button>
                    <Button 
                      size="sm" 
                      variant={event.my_rsvp === 'interested' ? 'default' : 'outline'}
                      onClick={() => handleRsvpEvent(event.event_id, 'interested')}
                      className={event.my_rsvp === 'interested' ? 'bg-blue-600' : ''}
                    >
                      Interested
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="p-12 text-center">
              <Calendar className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="font-semibold mb-2">No upcoming events</h3>
              <p className="text-gray-500">Create an event to bring the community together!</p>
            </Card>
          )}
        </div>
      );
    }

    // Media view
    if (activeSection === 'media') {
      return (
        <div className="space-y-4">
          {/* Add Media Button - For Members or users who can view content */}
          {user && canViewContent && (
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <label className="cursor-pointer">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" asChild>
                    <span>
                      {uploadingMedia ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Add Photo or Video
                    </span>
                  </Button>
                  <input
                    type="file"
                    accept="image/*,video/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadingMedia(true);
                      try {
                        let processedFile = file;
                        if (isHeicFile(file)) {
                          processedFile = await convertHeicToJpeg(file);
                        }
                        const formData = new FormData();
                        formData.append('file', processedFile);
                        const uploadRes = await api.post('/upload', formData);
                        
                        // Create a post with the media
                        const isVideo = file.type.startsWith('video/');
                        const postData = isVideo 
                          ? { text_body: '', video_url: uploadRes.data.url }
                          : { text_body: '', media_urls: [uploadRes.data.url] };
                        
                        await api.post(`/groups/${groupId}/posts`, postData);
                        fetchMedia();
                        fetchPosts();
                        toast.success('Media added to group!');
                      } catch (error) {
                        toast.error('Failed to upload media');
                      } finally {
                        setUploadingMedia(false);
                      }
                    }}
                    disabled={uploadingMedia}
                  />
                </label>
              </CardContent>
            </Card>
          )}

          {/* Sign in prompt for non-logged-in users */}
          {!user && canViewContent && (
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => navigate('/auth')}
                >
                  Sign in to Add Media
                </Button>
              </CardContent>
            </Card>
          )}

          {loadingMedia ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : media.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {media.map((item, idx) => (
                <div 
                  key={idx} 
                  className="aspect-square bg-gray-200 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition relative group"
                  onClick={() => setSelectedMediaItem(item)}
                >
                  {item.type === 'image' ? (
                    <img src={item.url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-900">
                      <Play className="h-10 w-10 text-white" />
                    </div>
                  )}
                  {/* Delete button - Admin only */}
                  {isAdmin && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm('Delete this media?')) {
                          handleDeletePost(item.post_id);
                          setMedia(media.filter((_, i) => i !== idx));
                        }
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <ImageIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="font-semibold mb-2">No media yet</h3>
              <p className="text-gray-500">Photos and videos shared in posts will appear here.</p>
            </Card>
          )}
        </div>
      );
    }

    // Files view
    if (activeSection === 'files') {
      return (
        <div className="space-y-4">
          {/* Upload File Button - For Members or users who can view content */}
          {user && canViewContent && (
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <label className="cursor-pointer">
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" asChild>
                    <span>
                      {uploadingFile ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4 mr-2" />
                      )}
                      Upload File
                    </span>
                  </Button>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploadingFile}
                  />
                </label>
              </CardContent>
            </Card>
          )}

          {/* Sign in prompt for non-logged-in users */}
          {!user && canViewContent && (
            <Card className="shadow-sm">
              <CardContent className="p-4">
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => navigate('/auth')}
                >
                  Sign in to Upload Files
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Files List */}
          {loadingFiles ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : files.length > 0 ? (
            <div className="space-y-2">
              {files.map(file => (
                <Card key={file.file_id} className="shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded bg-emerald-100 flex items-center justify-center">
                          {file.file_type === 'pdf' ? (
                            <FileDoc className="h-5 w-5 text-red-600" />
                          ) : file.file_type === 'spreadsheet' ? (
                            <FileDoc className="h-5 w-5 text-green-600" />
                          ) : (
                            <File className="h-5 w-5 text-emerald-600" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{file.name}</p>
                          <p className="text-sm text-gray-500">
                            {formatFileSize(file.size)} • Uploaded by {file.uploader_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <a 
                          href={file.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-gray-100 rounded-full"
                          title="Download file"
                        >
                          <Download className="h-4 w-4 text-gray-600" />
                        </a>
                        {/* Delete button - Admin only */}
                        {isAdmin && (
                          <button 
                            onClick={() => handleDeleteFile(file.file_id)}
                            className="p-2 hover:bg-red-50 rounded-full"
                            title="Delete file (Admin only)"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <File className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="font-semibold mb-2">No files yet</h3>
              <p className="text-gray-500">Upload documents to share with the group.</p>
            </Card>
          )}
        </div>
      );
    }

    // Activity Log view (Admin only)
    if (activeSection === 'activity-log' && (isAdmin || isModerator)) {
      return (
        <div className="space-y-4">
          <ActivityLogViewer groupId={groupId} />
        </div>
      );
    }

    // Scheduled Posts view (Admin only)
    if (activeSection === 'scheduled-posts' && (isAdmin || isModerator)) {
      return (
        <div className="space-y-4">
          <ScheduledPostsManager groupId={groupId} />
        </div>
      );
    }

    // Reported Content view (Admin only)
    if (activeSection === 'reported-content' && (isAdmin || isModerator)) {
      return (
        <div className="space-y-4">
          <ReportedContentViewer groupId={groupId} />
        </div>
      );
    }

    // Pending Posts view (Admin only)
    if (activeSection === 'pending-posts' && (isAdmin || isModerator)) {
      return (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Pending Posts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingPosts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No pending posts</p>
                  <p className="text-sm">Posts awaiting approval will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingPosts.map(post => (
                    <div key={post.post_id} className="p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={post.user_image} />
                          <AvatarFallback>{post.user_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-semibold">{post.user_name}</p>
                          <p className="text-sm text-gray-700 mt-1">{post.text_body}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" className="bg-emerald-600" onClick={() => handleApprovePost(post.post_id)}>
                          <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeletePost(post.post_id)}>
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    // Potential Spam view (Admin only)
    if (activeSection === 'spam' && (isAdmin || isModerator)) {
      return (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Potential Spam
              </CardTitle>
            </CardHeader>
            <CardContent>
              {posts.filter(p => p.is_spam).length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No spam detected</p>
                  <p className="text-sm">Posts flagged as potential spam will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {posts.filter(p => p.is_spam).map(post => (
                    <div key={post.post_id} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-start gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={post.user_image} />
                          <AvatarFallback>{post.user_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-semibold">{post.user_name}</p>
                          <p className="text-sm text-gray-700 mt-1">{post.text_body}</p>
                          {post.spam_reason && (
                            <Badge className="mt-2 bg-amber-100 text-amber-700">
                              {post.spam_reason}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => handleMarkNotSpam(post.post_id)}>
                          <Check className="h-4 w-4 mr-1" /> Not Spam
                        </Button>
                        <Button size="sm" className="bg-red-600" onClick={() => handleDeletePost(post.post_id)}>
                          <Trash2 className="h-4 w-4 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    // Default: Posts view
    return (
      <div className="space-y-4">
        {/* Create Post Box - Facebook Style */}
        {isMember && (
          <Card className="shadow-sm">
            <CardContent className="p-4">
              {/* Top Row - Avatar and Input */}
              <div className="flex gap-3 mb-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-emerald-100 text-emerald-700">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={() => setShowPostComposer(true)}
                  className="flex-1 text-left px-4 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-500 transition-colors"
                  data-testid="post-input-trigger"
                >
                  Write something...
                </button>
              </div>
              
              {/* Action Buttons Row */}
              <div className="flex items-center justify-around pt-2 border-t">
                <Button 
                  type="button" 
                  variant="ghost" 
                  className="flex-1 text-gray-600 hover:bg-gray-100"
                  onClick={() => {
                    setShowPostComposer(true);
                    setPostType('sale');
                  }}
                  data-testid="for-sale-btn"
                >
                  <Tag className="h-5 w-5 mr-2 text-teal-500" />
                  For sale
                </Button>
                <Button 
                  type="button" 
                  variant="ghost" 
                  className="flex-1 text-gray-600 hover:bg-gray-100"
                  onClick={() => setShowPostComposer(true)}
                >
                  <Smile className="h-5 w-5 mr-2 text-yellow-500" />
                  Feeling/activity
                </Button>
                <Button 
                  type="button" 
                  variant="ghost" 
                  className="flex-1 text-gray-600 hover:bg-gray-100 hidden sm:flex"
                  onClick={() => {
                    setShowPostComposer(true);
                    setPostType('poll');
                  }}
                >
                  <BarChart3 className="h-5 w-5 mr-2 text-red-500" />
                  Poll
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sign In Prompt - For Non-Members */}
        {!isMember && !user && (
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-gray-200 text-gray-500">
                    <User className="h-5 w-5" />
                  </AvatarFallback>
                </Avatar>
                <div 
                  className="flex-1 bg-gray-100 rounded-full px-4 py-2.5 text-gray-500 cursor-pointer hover:bg-gray-200 transition"
                  onClick={() => navigate('/auth')}
                >
                  Sign in to start a discussion...
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Join Prompt - For Logged In Non-Members */}
        {!isMember && user && (
          <Card className="shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-emerald-100 text-emerald-700">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div 
                  className="flex-1 bg-gray-100 rounded-full px-4 py-2.5 text-gray-500 cursor-pointer hover:bg-gray-200 transition"
                  onClick={handleJoin}
                >
                  Join group to start a discussion...
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Featured Section */}
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-700">Featured</span>
                <span className="text-gray-400 text-sm">ⓘ</span>
              </div>
              {isAdmin && (
                <Button variant="link" size="sm" className="text-emerald-600">Add</Button>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">Members won't see this section if it's empty.</p>
          </CardContent>
        </Card>

        {/* Sort Dropdown */}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Most relevant</span>
          <ChevronDown className="h-4 w-4" />
        </div>

        {/* Posts Feed */}
        {loadingPosts ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : posts.length > 0 ? (
          posts.map(post => (
            <GroupPost
              key={post.post_id}
              post={post}
              groupId={groupId}
              isAdmin={isAdmin}
              isModerator={isModerator}
              onDelete={handleDeletePost}
              onLike={handleLikePost}
              user={user}
            />
          ))
        ) : (
          <Card className="p-12 text-center">
            <MessageCircle className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="font-semibold mb-2">No posts yet</h3>
            <p className="text-gray-500">Be the first to share something!</p>
          </Card>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header user={user} setUser={setUser} />
      
      {/* Spacer for fixed header */}
      <div className="h-[184px]"></div>

      {!canViewContent ? (
        /* Private Group - Not a Member */
        <div className="container mx-auto px-4 py-8">
          <Card className="p-12 text-center max-w-2xl mx-auto">
            <div className="relative h-48 -mx-12 -mt-12 mb-6 rounded-t-lg overflow-hidden">
              <img src={group.cover_image || DEFAULT_COVER} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
            <Lock className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-2xl font-bold mb-2">{group.name}</h2>
            <p className="text-gray-500 mb-2">
              {group.privacy === 'private' ? 'Private' : 'Public'} Group · {group.member_count} members
            </p>
            <p className="text-gray-600 mb-6">{group.description}</p>
            {!user ? (
              <Button onClick={() => navigate('/auth')} size="lg">
                Sign in to Join
              </Button>
            ) : membershipStatus === 'pending' ? (
              <Button disabled variant="secondary" size="lg">
                Request Pending
              </Button>
            ) : (
              <Button onClick={handleJoin} size="lg" className="bg-emerald-600 hover:bg-emerald-700">
                Request to Join
              </Button>
            )}
          </Card>
        </div>
      ) : (
        /* Facebook-style Layout for Members */
        <div className="flex">
          {/* Left Sidebar - Admin Tools (hidden on mobile) */}
          <div className="hidden lg:block sticky top-[184px] h-[calc(100vh-184px)] overflow-y-auto">
            <AdminSidebar 
              group={group} 
              groupId={groupId}
              joinRequests={joinRequests}
              isAdmin={isAdmin}
              isModerator={isModerator}
              onNavigate={setActiveSection}
              pendingPosts={pendingPosts}
              reportedContent={reportedContent}
            />
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            {/* Cover Photo */}
            <div className="relative h-64 md:h-80 lg:h-96 bg-gray-900">
              <img 
                src={group.cover_image || DEFAULT_COVER} 
                alt={group.name}
                className="w-full h-full object-cover"
              />
              {isAdmin && (
                <Button 
                  size="sm" 
                  variant="secondary" 
                  className="absolute bottom-4 right-4 shadow-lg"
                  onClick={() => navigate(`/community/groups/${groupId}/settings`)}
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>

            {/* Group Info Section */}
            <div className="bg-white border-b shadow-sm">
              <div className="container mx-auto px-4 py-4">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{group.name}</h1>
                <div className="flex items-center gap-2 text-gray-600 mt-1">
                  {group.privacy === 'private' ? (
                    <Lock className="h-4 w-4" />
                  ) : (
                    <Globe className="h-4 w-4" />
                  )}
                  <span className="text-sm">{group.privacy === 'private' ? 'Private' : 'Public'} group</span>
                  <span className="text-gray-400">·</span>
                  <span className="text-sm">{group.member_count?.toLocaleString()} members</span>
                </div>

                {/* Member Avatars Row */}
                <div className="flex items-center gap-2 mt-3">
                  <div className="flex -space-x-2">
                    {members.slice(0, 10).map((member, idx) => (
                      <Avatar key={member.user_id} className="h-8 w-8 border-2 border-white">
                        <AvatarImage src={member.user_image} />
                        <AvatarFallback className="text-xs bg-emerald-100">
                          {member.user_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                    ))}
                  </div>
                  {members.length > 10 && (
                    <span className="text-sm text-gray-500">+{members.length - 10} more</span>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 mt-4">
                  {(isAdmin || isModerator) && (
                    <Button 
                      onClick={() => setShowInviteDialog(true)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Invite
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleShareGroup}>
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                  {/* Show Join button for non-members */}
                  {!isMember && user && (
                    <Button 
                      onClick={handleJoin}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Join Group
                    </Button>
                  )}
                  {/* Show Sign in prompt for guests */}
                  {!isMember && !user && (
                    <Button 
                      onClick={() => navigate('/login')}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Sign in to Join
                    </Button>
                  )}
                  {/* Show Joined dropdown for members */}
                  {isMember && user && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="secondary">
                          <Check className="h-4 w-4 mr-2" />
                          Joined
                          <ChevronDown className="h-4 w-4 ml-2" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {isAdmin && (
                          <DropdownMenuItem onClick={() => navigate(`/community/groups/${groupId}/settings`)}>
                            <Settings className="h-4 w-4 mr-2" />
                            Group Settings
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLeave} className="text-red-600">
                          <UserMinus className="h-4 w-4 mr-2" />
                          Leave Group
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                {/* Navigation Tabs - Facebook Style */}
                <div className="flex items-center gap-1 mt-4 border-t pt-2 -mb-4 overflow-x-auto scrollbar-hide">
                  <button 
                    onClick={() => setActiveSection('about')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeSection === 'about' 
                        ? 'border-blue-600 text-blue-600' 
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    About
                  </button>
                  <button 
                    onClick={() => setActiveSection('posts')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeSection === 'posts' 
                        ? 'border-blue-600 text-blue-600' 
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Discussion
                  </button>
                  <button 
                    onClick={() => setActiveSection('buyandsell')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeSection === 'buyandsell' 
                        ? 'border-blue-600 text-blue-600' 
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Buy and Sell
                  </button>
                  <button 
                    onClick={() => setActiveSection('featured')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeSection === 'featured' 
                        ? 'border-blue-600 text-blue-600' 
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Featured
                  </button>
                  <button 
                    onClick={() => setActiveSection('members')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeSection === 'members' 
                        ? 'border-blue-600 text-blue-600' 
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Members
                  </button>
                  <button 
                    onClick={() => setActiveSection('events')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeSection === 'events' 
                        ? 'border-blue-600 text-blue-600' 
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Events
                  </button>
                  <button 
                    onClick={() => setActiveSection('media')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeSection === 'media' 
                        ? 'border-blue-600 text-blue-600' 
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Media
                  </button>
                  <button 
                    onClick={() => setActiveSection('files')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeSection === 'files' 
                        ? 'border-blue-600 text-blue-600' 
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Files
                  </button>
                  <div className="ml-auto flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => setShowSearchDialog(true)}
                      title="Search in group"
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => {
                          navigator.clipboard.writeText(window.location.href);
                          toast.success('Link copied!');
                        }}>
                          <Link2 className="h-4 w-4 mr-2" />
                          Copy Link
                        </DropdownMenuItem>
                        {isMember && (
                          <>
                            <DropdownMenuItem onClick={() => setActiveSection('members')}>
                              <Users className="h-4 w-4 mr-2" />
                              View Members
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => {
                              toast.info('Notifications muted for this group');
                            }}>
                              <Bell className="h-4 w-4 mr-2" />
                              Mute Notifications
                            </DropdownMenuItem>
                          </>
                        )}
                        {isAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => navigate(`/community/groups/${groupId}/settings`)}>
                              <Settings className="h-4 w-4 mr-2" />
                              Group Settings
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => toast.info('Report submitted')} className="text-red-600">
                          <Flag className="h-4 w-4 mr-2" />
                          Report Group
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="container mx-auto px-4 py-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Feed */}
                <div className="lg:col-span-2">
                  {renderContent()}
                </div>

                {/* Right Sidebar - About */}
                <div className="space-y-4">
                  <Card className="shadow-sm">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">About</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-gray-600 text-sm">{group.description}</p>
                      
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        {group.privacy === 'private' ? (
                          <>
                            <Lock className="h-4 w-4" />
                            <div>
                              <p className="font-medium">Private</p>
                              <p className="text-xs text-gray-500">Only members can see who's in the group and what they post.</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <Globe className="h-4 w-4" />
                            <div>
                              <p className="font-medium">Public</p>
                              <p className="text-xs text-gray-500">Anyone can see who's in the group and what they post.</p>
                            </div>
                          </>
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Eye className="h-4 w-4" />
                        <div>
                          <p className="font-medium">Visible</p>
                          <p className="text-xs text-gray-500">Anyone can find this group.</p>
                        </div>
                      </div>

                      <Button variant="outline" className="w-full mt-2">
                        Learn more about this group
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Back to Groups */}
                  <Card className="shadow-sm">
                    <CardContent className="p-4">
                      <Link to="/community/groups" className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium">
                        <ArrowLeft className="h-4 w-4" />
                        Back to Groups
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Members</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-500 mb-4">
              Enter user IDs to invite (comma separated)
            </p>
            <Input
              placeholder="user_abc123, user_def456"
              value={inviteUserIds}
              onChange={(e) => setInviteUserIds(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={async () => {
              const userIds = inviteUserIds.split(',').map(id => id.trim()).filter(Boolean);
              if (userIds.length === 0) {
                toast.error('Please enter at least one user ID');
                return;
              }
              try {
                const res = await api.post(`/groups/${groupId}/invite`, { user_ids: userIds });
                toast.success(res.data.message);
                setShowInviteDialog(false);
                setInviteUserIds('');
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to send invites');
              }
            }}>
              Send Invites
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Event Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Event</DialogTitle>
            <DialogDescription>
              Create a new event for the group members
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateEvent} className="space-y-4 py-4">
            <div>
              <Label htmlFor="event-title">Event Title *</Label>
              <Input
                id="event-title"
                value={eventForm.title}
                onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="What's the event?"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="event-description">Description</Label>
              <Textarea
                id="event-description"
                value={eventForm.description}
                onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Tell people what the event is about"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="event-start">Start Date & Time *</Label>
                <Input
                  id="event-start"
                  type="datetime-local"
                  value={eventForm.start_date}
                  onChange={(e) => setEventForm(prev => ({ ...prev, start_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="event-end">End Date & Time</Label>
                <Input
                  id="event-end"
                  type="datetime-local"
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm(prev => ({ ...prev, end_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="is-online"
                  checked={eventForm.is_online}
                  onCheckedChange={(v) => setEventForm(prev => ({ ...prev, is_online: v }))}
                />
                <Label htmlFor="is-online">Online Event</Label>
              </div>
            </div>
            
            {eventForm.is_online ? (
              <div>
                <Label htmlFor="event-link">Meeting Link</Label>
                <Input
                  id="event-link"
                  value={eventForm.online_link}
                  onChange={(e) => setEventForm(prev => ({ ...prev, online_link: e.target.value }))}
                  placeholder="https://zoom.us/..."
                  className="mt-1"
                />
              </div>
            ) : (
              <div>
                <Label htmlFor="event-location">Location</Label>
                <Input
                  id="event-location"
                  value={eventForm.location}
                  onChange={(e) => setEventForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Where is the event?"
                  className="mt-1"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="max-attendees">Max Attendees (Optional)</Label>
              <Input
                id="max-attendees"
                type="number"
                value={eventForm.max_attendees}
                onChange={(e) => setEventForm(prev => ({ ...prev, max_attendees: e.target.value }))}
                placeholder="Unlimited"
                className="mt-1"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEventDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submittingEvent} className="bg-emerald-600 hover:bg-emerald-700">
                {submittingEvent && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create Event
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Media Viewer Dialog */}
      <Dialog open={!!selectedMediaItem} onOpenChange={() => setSelectedMediaItem(null)}>
        <DialogContent className="max-w-4xl p-0">
          {selectedMediaItem?.type === 'image' ? (
            <img 
              src={selectedMediaItem.url} 
              alt="" 
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          ) : selectedMediaItem?.type === 'video' ? (
            <video 
              src={selectedMediaItem.url} 
              controls 
              autoPlay 
              className="w-full h-auto max-h-[80vh]"
            />
          ) : null}
          {selectedMediaItem && (
            <div className="p-4 bg-white border-t">
              <p className="text-sm text-gray-500">
                Posted by {selectedMediaItem.user_name} • {formatRelativeTime(selectedMediaItem.created_at)}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Search Dialog */}
      <Dialog open={showSearchDialog} onOpenChange={setShowSearchDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Search in Group</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search posts, members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} disabled={searching}>
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            
            {searchResults.length > 0 && (
              <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                {searchResults.map(post => (
                  <div 
                    key={post.post_id} 
                    className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      setShowSearchDialog(false);
                      setSearchQuery('');
                      setSearchResults([]);
                      setActiveSection('posts');
                    }}
                  >
                    <p className="font-medium text-sm">{post.user_name}</p>
                    <p className="text-sm text-gray-600 line-clamp-2">{post.text_body}</p>
                  </div>
                ))}
              </div>
            )}
            
            {searchQuery && searchResults.length === 0 && !searching && (
              <p className="mt-4 text-center text-gray-500 text-sm">No results found</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Post Composer Dialog - Facebook Style */}
      <Dialog open={showPostComposer} onOpenChange={(open) => {
        setShowPostComposer(open);
        if (!open) {
          setPostType('discussion');
          setShowAddToPostMenu(false);
          setFeelingActivity(null);
          setCheckInLocation('');
          setPollOptions(['', '']);
        }
      }}>
        <DialogContent className="max-w-lg p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle className="text-center">Create post</DialogTitle>
          </DialogHeader>
          
          <div className="p-4">
            {/* User Info */}
            <div className="flex items-center gap-3 mb-4">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="bg-emerald-100 text-emerald-700">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold">{user?.name}</p>
                <Badge variant="outline" className="text-xs">
                  {group?.privacy === 'private' ? <Lock className="h-3 w-3 mr-1" /> : <Globe className="h-3 w-3 mr-1" />}
                  {group?.privacy === 'private' ? 'Private group' : 'Public group'}
                </Badge>
              </div>
            </div>
            
            {/* Post Input */}
            <Textarea
              placeholder="Write something..."
              value={newPost}
              onChange={(e) => setNewPost(e.target.value)}
              className="min-h-[120px] border-0 text-lg resize-none focus:ring-0 p-0"
              data-testid="post-composer-input"
            />
            
            {/* Feeling/Activity Badge */}
            {feelingActivity && (
              <div className="flex items-center gap-2 mt-2">
                <Badge className="bg-yellow-100 text-yellow-700">
                  {feelingActivity}
                  <button onClick={() => setFeelingActivity(null)} className="ml-2">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              </div>
            )}
            
            {/* Check-in Location */}
            {checkInLocation && (
              <div className="flex items-center gap-2 mt-2">
                <Badge className="bg-red-100 text-red-700">
                  <MapPin className="h-3 w-3 mr-1" />
                  {checkInLocation}
                  <button onClick={() => setCheckInLocation('')} className="ml-2">
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              </div>
            )}
            
            {/* Poll Options */}
            {postType === 'poll' && (
              <div className="mt-4 space-y-2">
                <p className="font-medium text-sm">Poll Options</p>
                {pollOptions.map((option, idx) => (
                  <Input
                    key={idx}
                    placeholder={`Option ${idx + 1}`}
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...pollOptions];
                      newOptions[idx] = e.target.value;
                      setPollOptions(newOptions);
                    }}
                  />
                ))}
                {pollOptions.length < 5 && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setPollOptions([...pollOptions, ''])}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add option
                  </Button>
                )}
              </div>
            )}
            
            {/* Media Preview */}
            {postMediaUrls.length > 0 && (
              <div className="flex gap-2 mt-4 flex-wrap">
                {postMediaUrls.map((url, idx) => (
                  <div key={idx} className="relative">
                    <img src={url} alt="" className="h-20 w-20 object-cover rounded" />
                    <button
                      onClick={() => setPostMediaUrls(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute -top-2 -right-2 bg-gray-800 text-white rounded-full p-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Link Preview */}
            {(linkPreview || fetchingLinkPreview) && (
              <div className="mt-4 border rounded-lg overflow-hidden" data-testid="composer-link-preview">
                {fetchingLinkPreview ? (
                  <div className="flex items-center gap-3 p-3">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                    <span className="text-sm text-gray-500">Fetching link preview...</span>
                  </div>
                ) : linkPreview && (
                  <div className="relative">
                    {linkPreview.image && (
                      <img src={linkPreview.image} alt="" className="w-full h-40 object-cover" />
                    )}
                    <div className="p-3">
                      <p className="text-xs text-gray-500 uppercase">{linkPreview.site_name}</p>
                      <p className="font-semibold">{linkPreview.title}</p>
                      {linkPreview.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">{linkPreview.description}</p>
                      )}
                    </div>
                    <button
                      onClick={() => setLinkPreview(null)}
                      className="absolute top-2 right-2 p-1 bg-gray-800/60 text-white rounded-full"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* Selected Listing */}
            {selectedListing && (
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-3">
                  {selectedListing.images?.[0] && (
                    <img src={selectedListing.images[0]} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  )}
                  <div className="flex-1">
                    <Badge className="bg-emerald-500 text-white text-xs mb-1">For Sale</Badge>
                    <p className="font-medium">{selectedListing.title}</p>
                    <p className="text-emerald-600 font-bold">${selectedListing.price?.toLocaleString()}</p>
                  </div>
                  <button onClick={() => setSelectedListing(null)} className="p-1 hover:bg-emerald-100 rounded-full">
                    <X className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Add to Post Options */}
          {showAddToPostMenu ? (
            <div className="p-4 border-t">
              <div className="flex items-center gap-2 mb-4">
                <button onClick={() => setShowAddToPostMenu(false)} className="p-1 hover:bg-gray-100 rounded-full">
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <span className="font-semibold">Add to your post</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <div className="p-2 bg-green-100 rounded-full">
                    <ImageIcon className="h-5 w-5 text-green-600" />
                  </div>
                  <span>Photo/video</span>
                  <input 
                    type="file" 
                    accept={ACCEPTED_IMAGE_FORMATS}
                    multiple
                    className="hidden" 
                    onChange={handleMediaUpload}
                  />
                </label>
                
                <button 
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left"
                  onClick={() => {
                    const people = prompt('Tag people (comma separated):');
                    if (people) setTaggedPeople(people.split(',').map(p => p.trim()));
                    setShowAddToPostMenu(false);
                  }}
                >
                  <div className="p-2 bg-blue-100 rounded-full">
                    <AtSign className="h-5 w-5 text-blue-600" />
                  </div>
                  <span>Tag people</span>
                </button>
                
                <button 
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left"
                  onClick={() => {
                    const location = prompt('Check in location:');
                    if (location) setCheckInLocation(location);
                    setShowAddToPostMenu(false);
                  }}
                >
                  <div className="p-2 bg-red-100 rounded-full">
                    <MapPin className="h-5 w-5 text-red-600" />
                  </div>
                  <span>Check in</span>
                </button>
                
                <button 
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left"
                  onClick={() => {
                    const feeling = prompt('How are you feeling?');
                    if (feeling) setFeelingActivity(feeling);
                    setShowAddToPostMenu(false);
                  }}
                >
                  <div className="p-2 bg-yellow-100 rounded-full">
                    <Smile className="h-5 w-5 text-yellow-600" />
                  </div>
                  <span>Feeling/activity</span>
                </button>
                
                <button 
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left"
                  onClick={() => {
                    setPostType('poll');
                    setShowAddToPostMenu(false);
                  }}
                >
                  <div className="p-2 bg-red-100 rounded-full">
                    <BarChart3 className="h-5 w-5 text-red-600" />
                  </div>
                  <span>Poll</span>
                </button>
                
                <button 
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left"
                  onClick={() => {
                    setActiveSection('events');
                    setShowPostComposer(false);
                    setShowEventDialog(true);
                  }}
                >
                  <div className="p-2 bg-purple-100 rounded-full">
                    <Calendar className="h-5 w-5 text-purple-600" />
                  </div>
                  <span>Create event</span>
                </button>
                
                <button 
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg text-left"
                  onClick={() => {
                    setShowPostComposer(false);
                    setShowCreateListingDialog(true);
                  }}
                >
                  <div className="p-2 bg-teal-100 rounded-full">
                    <Tag className="h-5 w-5 text-teal-600" />
                  </div>
                  <span>Sell item</span>
                </button>
                
                <label className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer">
                  <div className="p-2 bg-gray-100 rounded-full">
                    <File className="h-5 w-5 text-gray-600" />
                  </div>
                  <span>File</span>
                  <input type="file" className="hidden" onChange={handleMediaUpload} />
                </label>
              </div>
            </div>
          ) : (
            <div className="p-4 border-t">
              <div className="flex items-center justify-between p-3 border rounded-lg mb-4">
                <span className="text-sm font-medium">Add to your post</span>
                <div className="flex items-center gap-2">
                  <label className="p-2 hover:bg-gray-100 rounded-full cursor-pointer">
                    <ImageIcon className="h-5 w-5 text-green-600" />
                    <input 
                      type="file" 
                      accept={ACCEPTED_IMAGE_FORMATS}
                      multiple
                      className="hidden" 
                      onChange={handleMediaUpload}
                    />
                  </label>
                  <button 
                    className="p-2 hover:bg-gray-100 rounded-full"
                    onClick={() => {
                      const people = prompt('Tag people:');
                      if (people) setTaggedPeople(people.split(',').map(p => p.trim()));
                    }}
                  >
                    <AtSign className="h-5 w-5 text-blue-600" />
                  </button>
                  <button 
                    className="p-2 hover:bg-gray-100 rounded-full"
                    onClick={() => {
                      const location = prompt('Check in:');
                      if (location) setCheckInLocation(location);
                    }}
                  >
                    <MapPin className="h-5 w-5 text-red-600" />
                  </button>
                  <button 
                    className="p-2 hover:bg-gray-100 rounded-full"
                    onClick={() => {
                      const feeling = prompt('Feeling:');
                      if (feeling) setFeelingActivity(feeling);
                    }}
                  >
                    <Smile className="h-5 w-5 text-yellow-600" />
                  </button>
                  <button 
                    className="p-2 hover:bg-gray-100 rounded-full"
                    onClick={() => setShowAddToPostMenu(true)}
                  >
                    <MoreHorizontal className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>
              
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={submittingPost || (!newPost.trim() && postMediaUrls.length === 0 && !selectedListing && postType !== 'poll')}
                onClick={async (e) => {
                  await handleSubmitPost(e);
                  setShowPostComposer(false);
                }}
              >
                {submittingPost ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Post
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Listing Dialog */}
      <Dialog open={showCreateListingDialog} onOpenChange={setShowCreateListingDialog}>
        <DialogContent className="max-w-lg">
          {!listingType ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-center">Create new listing</DialogTitle>
                <DialogDescription className="text-center">
                  Choose the type of listing you want to create
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-4">
                <button
                  onClick={() => setListingType('item')}
                  className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <div className="p-4 bg-red-100 rounded-full">
                    <Tag className="h-8 w-8 text-red-600" />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">Item for sale</p>
                    <p className="text-xs text-gray-500">Create a single listing for one or more items to sell.</p>
                  </div>
                </button>
                
                <button
                  onClick={() => setListingType('vehicle')}
                  className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <div className="p-4 bg-teal-100 rounded-full">
                    <Car className="h-8 w-8 text-teal-600" />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">Vehicle for sale</p>
                    <p className="text-xs text-gray-500">Sell a car, truck or other type of vehicle.</p>
                  </div>
                </button>
                
                <button
                  onClick={() => setListingType('home')}
                  className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <div className="p-4 bg-blue-100 rounded-full">
                    <Building className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">Home for sale or rent</p>
                    <p className="text-xs text-gray-500">List a house or apartment for sale or rent.</p>
                  </div>
                </button>
                
                <button
                  onClick={() => setListingType('sale_event')}
                  className="flex flex-col items-center gap-3 p-6 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all"
                >
                  <div className="p-4 bg-orange-100 rounded-full">
                    <PartyPopper className="h-8 w-8 text-orange-600" />
                  </div>
                  <div className="text-center">
                    <p className="font-semibold">Sale event</p>
                    <p className="text-xs text-gray-500">Garage sales, estate sales, moving sales, swap meets and more.</p>
                  </div>
                </button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <button onClick={() => setListingType(null)} className="p-1 hover:bg-gray-100 rounded-full">
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <DialogTitle>
                    {listingType === 'item' && 'Item for sale'}
                    {listingType === 'vehicle' && 'Vehicle for sale'}
                    {listingType === 'home' && 'Home for sale or rent'}
                    {listingType === 'sale_event' && 'Sale event'}
                  </DialogTitle>
                </div>
              </DialogHeader>
              
              <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                {/* Images Upload */}
                <div>
                  <Label>Photos</Label>
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {newListingData.images.map((img, idx) => (
                      <div key={idx} className="relative aspect-square">
                        <img src={img} alt="" className="w-full h-full object-cover rounded-lg" />
                        <button
                          onClick={() => setNewListingData(prev => ({
                            ...prev,
                            images: prev.images.filter((_, i) => i !== idx)
                          }))}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {newListingData.images.length < 8 && (
                      <label className="aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50">
                        <Plus className="h-6 w-6 text-gray-400" />
                        <span className="text-xs text-gray-500">Add photos</span>
                        <input 
                          type="file" 
                          accept={ACCEPTED_IMAGE_FORMATS}
                          multiple
                          className="hidden" 
                          onChange={handleListingImageUpload}
                        />
                      </label>
                    )}
                  </div>
                </div>
                
                {/* Title */}
                <div>
                  <Label htmlFor="listing-title">Title *</Label>
                  <Input
                    id="listing-title"
                    placeholder="What are you selling?"
                    value={newListingData.title}
                    onChange={(e) => setNewListingData(prev => ({ ...prev, title: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                
                {/* Price */}
                <div>
                  <Label htmlFor="listing-price">Price *</Label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="listing-price"
                      type="number"
                      placeholder="0.00"
                      value={newListingData.price}
                      onChange={(e) => setNewListingData(prev => ({ ...prev, price: e.target.value }))}
                      className="pl-8"
                    />
                  </div>
                </div>
                
                {/* Category */}
                <div>
                  <Label htmlFor="listing-category">Category</Label>
                  <Select 
                    value={newListingData.category} 
                    onValueChange={(val) => setNewListingData(prev => ({ ...prev, category: val }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {GROUP_CATEGORIES.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.icon} {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Condition */}
                <div>
                  <Label htmlFor="listing-condition">Condition</Label>
                  <Select 
                    value={newListingData.condition} 
                    onValueChange={(val) => setNewListingData(prev => ({ ...prev, condition: val }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="like_new">Like New</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Description */}
                <div>
                  <Label htmlFor="listing-desc">Description</Label>
                  <Textarea
                    id="listing-desc"
                    placeholder="Describe what you're selling..."
                    value={newListingData.description}
                    onChange={(e) => setNewListingData(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1"
                    rows={4}
                  />
                </div>
                
                {/* Location */}
                <div>
                  <Label htmlFor="listing-location">Location</Label>
                  <div className="relative mt-1">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="listing-location"
                      placeholder="City, State"
                      value={newListingData.location}
                      onChange={(e) => setNewListingData(prev => ({ ...prev, location: e.target.value }))}
                      className="pl-8"
                    />
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setListingType(null);
                  setNewListingData({
                    title: '',
                    description: '',
                    price: '',
                    category: '',
                    condition: 'new',
                    images: [],
                    location: ''
                  });
                }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreateListing}
                  disabled={creatingListing || !newListingData.title || !newListingData.price}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {creatingListing && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Post Listing
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GroupDetailPage;
